// client/src/pages/earn/Lootably.tsx
import { useAuth } from "@/hooks/use-auth";
import { Card, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Terminal } from "lucide-react";
import { useState } from "react";

const PLACEMENT_ID = "cmfrteifd023k01xf9deu6mdb";
const LOOTABLY_BASE_URL = "https://wall.lootably.com/";

// The navigation bar has a height of 4rem (from the pt-16 class in App.tsx)
const NAVBAR_HEIGHT = "4rem"; 

export function LootablyOfferwallPage() {
  const { user, isAuthLoading } = useAuth();
  const [isIframeLoading, setIsIframeLoading] = useState(true);

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex items-center space-x-4">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          <span className="text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="p-4 md:p-8">
        <Alert variant="destructive">
          <Terminal className="h-4 w-4" />
          <AlertTitle>Authentication Required</AlertTitle>
          <AlertDescription>
            You must be logged in to view offers. Please log in and try again.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const offerwallUrl = `${LOOTABLY_BASE_URL}?placementID=${PLACEMENT_ID}&sid=${user.id}`;

  return (
    // Create a container with a height calculated to fill the screen below the navbar
    <div 
      className="flex flex-col p-4 md:p-8"
      style={{ height: `calc(100vh - ${NAVBAR_HEIGHT})` }}    >
      {/* Page Header (does not grow) */}
      <div className="flex-shrink-0 mb-4">
        <h2 className="text-2xl font-bold tracking-tight">Lootably Offerwall</h2>
        <p className="text-muted-foreground">
          Complete tasks and offers below to earn tokens. Rewards are credited automatically.
        </p>
      </div>

      {/* This container grows to fill the remaining space and establishes a flex context */}
      <div className="flex-1 flex flex-col min-h-0">
        {/* The Card and its content grow to fill the container */}
        <Card className="flex-1 flex flex-col">
          <CardContent className="flex-1 p-1 relative">
            {isIframeLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background rounded-md">
                <div className="flex flex-col items-center gap-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
                  <p className="text-muted-foreground">Loading offers...</p>
                </div>
              </div>
            )}
            <iframe
              title="Lootably Offer Wall"
              allow="clipboard-write; fullscreen"
              src={offerwallUrl}
              onLoad={() => setIsIframeLoading(false)}
              className={`w-full h-full border-0 rounded-md transition-opacity duration-500 ${isIframeLoading ? 'opacity-0' : 'opacity-100'}`}
            />
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
